import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { defaultMessages, type LocaleCatalog } from "./defaultMessages";
import {
  getCurrentLocale,
  setCurrentLocale,
  type AppLocale,
} from "./locale";
import { loadRemoteMessages, mergeMessages } from "./remoteConfig";
import { decodeHtmlEntities } from "./htmlEntities";

type TranslationValues = Record<string, string | number>;

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: (key: string, values?: TranslationValues) => string;
  messages: LocaleCatalog;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => getCurrentLocale());

  const { data: remoteMessages } = useQuery({
    queryKey: ["remoteConfig", "i18n"],
    queryFn: loadRemoteMessages,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const messages = useMemo(
    () => mergeMessages(remoteMessages),
    [remoteMessages],
  );

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setCurrentLocale(nextLocale);
    setLocaleState(nextLocale);
  }, []);

  useEffect(() => {
    setCurrentLocale(locale);
  }, [locale]);

  const t = useCallback(
    (key: string, values?: TranslationValues) => {
      const template =
        messages[locale][key] || messages.en[key] || defaultMessages.en[key] || key;
      const decodedTemplate = decodeHtmlEntities(template);

      if (!values) return decodedTemplate;

      return Object.entries(values).reduce(
        (result, [name, value]) =>
          result.replaceAll(`{${name}}`, String(value)),
        decodedTemplate,
      );
    },
    [locale, messages],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, messages }),
    [locale, messages, setLocale, t],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}
